import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import useMountEffect from '../../hooks/useMountEffect';
import InlineTreeFilter from './InlineTreeFilter';
import {
	fetchLocationsWithRegionsAndSubRegionsForBuyer,
	fetchLocationsWithRegionsAndSubRegionsForSupplier,
} from '../../thunks/locations_thunks';
import { LocationDropdownRecord } from '../name_component/location/LocationDropdownRecord';
import { ROLE_TYPES } from '../../utils/DataConstants';

const defaultPagination = { current: 1, pageSize: 1000 };
const sortBy = { sort_by: 'name', order: 'ascend' };

const addKeys = (locations: any[]): any[] => {
	if (!Array.isArray(locations)) {
		return [];
	}
	return locations.map((location) => {
		const hasChildren = Array.isArray(location.children) && location.children.length > 0;
		return {
			...location,
			title: hasChildren ? location.name : <LocationDropdownRecord record={location} />,
			key: location.id,
			value: location.id,
			children: hasChildren ? addKeys(location.children) : [],
		};
	});
};

interface LocationsInlineFilterProps {
	targetCollectionName: string;
	value?: Array<string | number>;
	onChange?: (values: Array<string | number>) => void;
	placeholder?: string;
	userType: string;
	fetchLocationsForBuyer: (
		params: any,
		targetCollectionName: string,
		pagination?: any,
		sorting?: any,
		filters?: any,
		addToTargetCollection?: boolean
	) => Promise<any[]>;
	fetchLocationsForSupplier: (
		params: any,
		targetCollectionName: string,
		pagination?: any,
		sorting?: any,
		filters?: any,
		addToTargetCollection?: boolean
	) => Promise<any[]>;
}

const LocationsInlineFilter: React.FC<LocationsInlineFilterProps> = ({
	targetCollectionName,
	value,
	onChange,
	placeholder = 'Search for locations',
	userType,
	fetchLocationsForBuyer,
	fetchLocationsForSupplier,
}) => {
	const [treeData, setTreeData] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);

	const loadLocations = useCallback(
		(searchText: string) => {
			const fetcher =
				userType === ROLE_TYPES.SUPPLIER ? fetchLocationsForSupplier : fetchLocationsForBuyer;
			setLoading(true);
			fetcher(
				{ name: searchText, noIsActiveFilter: true },
				targetCollectionName,
				defaultPagination,
				sortBy,
				undefined,
				false
			)
				.then((arrData) => {
					setTreeData(addKeys(arrData || []));
					setLoading(false);
				})
				.catch(() => setLoading(false));
		},
		[userType, fetchLocationsForBuyer, fetchLocationsForSupplier, targetCollectionName]
	);

	useMountEffect(() => {
		loadLocations('');
	});

	return (
		<InlineTreeFilter
			treeData={treeData}
			value={value}
			onChange={onChange}
			onSearch={loadLocations}
			placeholder={placeholder}
			searchProp="name"
			loading={loading}
		/>
	);
};

const mapStateToProps = (state: any) => ({
	userType: state.session.userType,
});

const mapDispatchToProps = (dispatch: any) => ({
	fetchLocationsForBuyer: (
		params: any,
		targetCollectionName: string,
		pagination: any,
		sorting: any,
		filters: any,
		addToTargetCollection: boolean
	) =>
		dispatch(
			fetchLocationsWithRegionsAndSubRegionsForBuyer(
				{ ...params, noIsActiveFilter: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchLocationsForSupplier: (
		params: any,
		targetCollectionName: string,
		pagination: any,
		sorting: any,
		filters: any,
		addToTargetCollection: boolean
	) =>
		dispatch(
			fetchLocationsWithRegionsAndSubRegionsForSupplier(
				{ ...params, noIsActiveFilter: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(LocationsInlineFilter);
